import {
  Box,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useActivitySites } from '../hooks/useActivitySites';

const STATUS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Booked', value: 'Booked' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
  { label: 'Cancelled', value: 'Cancelled' },
];

const TYPE_OPTIONS: { label: string; value: '' | 'Labour' | 'Variation' }[] = [
  { label: 'All', value: '' },
  { label: 'Labour', value: 'Labour' },
  { label: 'Variations', value: 'Variation' },
];

interface ActivityFilterContentProps {
  status: string;
  onStatusChange: (status: string) => void;
  siteIds: number[];
  onSitesChange: (ids: number[]) => void;
  type: '' | 'Labour' | 'Variation';
  onTypeChange: (type: '' | 'Labour' | 'Variation') => void;
}

function FilterChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      sx={{
        height: 28,
        borderRadius: 1.5,
        cursor: 'pointer',
        fontWeight: selected ? 600 : 400,
        color: 'text.primary',
        bgcolor: selected ? 'action.selected' : 'transparent',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    />
  );
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{ display: 'block', mb: 0.75 }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export function ActivityFilterContent({
  status,
  onStatusChange,
  siteIds,
  onSitesChange,
  type,
  onTypeChange,
}: ActivityFilterContentProps) {
  const { data: sites = [], isLoading: sitesLoading } = useActivitySites();
  const hasSites = sites.length > 0;
  return (
    <Stack spacing={2} sx={{ py: 0.5 }}>
      <FilterSection title="Status">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {STATUS_OPTIONS.map(option => (
            <FilterChip
              key={option.value || 'all'}
              label={option.label}
              selected={status === option.value}
              onClick={() => onStatusChange(option.value)}
            />
          ))}
        </Box>
      </FilterSection>

      <FilterSection title="Type">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {TYPE_OPTIONS.map(option => (
            <FilterChip
              key={option.value || 'all'}
              label={option.label}
              selected={type === option.value}
              onClick={() => onTypeChange(option.value)}
            />
          ))}
        </Box>
      </FilterSection>

      <FilterSection title="Sites">
        <Select
          multiple
          size="small"
          fullWidth
          displayEmpty
          disabled={!hasSites}
          value={siteIds}
          onChange={e => onSitesChange(e.target.value as number[])}
          input={<OutlinedInput />}
          renderValue={selected => {
            const ids = selected as number[];
            if (ids.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  {sitesLoading
                    ? 'Loading sites…'
                    : hasSites
                      ? 'All sites'
                      : 'No sites available'}
                </Typography>
              );
            }
            if (ids.length === 1) {
              return sites.find(s => s.id === ids[0])?.name ?? '1 site';
            }
            return `${ids.length} sites`;
          }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
          data-testid="activity-site-filter"
        >
          {sites.map(site => (
            <MenuItem key={site.id} value={site.id} sx={{ py: 0.25 }}>
              <Checkbox size="small" checked={siteIds.includes(site.id)} />
              <ListItemText primary={site.name} />
            </MenuItem>
          ))}
        </Select>
      </FilterSection>
    </Stack>
  );
}
